import { useI18n } from '../../../i18n';
// ============================================
// TopLostItemsTable - Top sản phẩm thất thoát
// (v1: items của session ABANDONED — khách rời cửa hàng không thanh toán;
//  movement LOSS sẽ gộp ở v2 phía BE, cùng shape)
// ============================================

import { Pagination } from '../../../components/shared';
import { useClientPagination } from '../../../hooks/useClientPagination';
import { cn } from '../../../utils/cn';
import { useMoney } from '../../../providers/CurrencyProvider';
// APP_CURRENCY vẫn cần làm fallback cho `Intl.NumberFormat` khi bản ghi không khai currency.
import { APP_CURRENCY } from '../../../utils/currency';

export interface TopLostItem {
  code: string;
  name: string;
  /** Tổng số lượng thất thoát; hàng cân ký có thể lẻ (vd 0.65). */
  quantityLost: number;
  /** Số vụ ghi nhận. */
  incidents: number;
  /** Giá trị thất thoát theo giá bán. */
  lostValue: number;
  /** Mã tiền tệ ISO của lostValue; thiếu -> fallback APP_CURRENCY. */
  currency?: string | null;
  lastIncidentAt?: string | null;
}

interface Props {
  items: TopLostItem[];
  /** Số dòng mỗi trang. */
  pageSize?: number;
  className?: string;
}

export function TopLostItemsTable({ items, pageSize = 10, className }: Props) {
  const { t } = useI18n();
  const money = useMoney();
  const page = useClientPagination(items, pageSize);

  // fmtMoney chuyển vào trong component vì nhánh fallback nay dùng hook `useMoney()`.
  const fmtMoney = (v: number, currency?: string | null) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || APP_CURRENCY,
        maximumFractionDigits: 0,
      }).format(v);
    } catch {
      // BE trả mã tiền tệ không hợp lệ -> đừng vỡ bảng, fallback tiền tệ của phạm vi đang xem
      return money.format(v, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    }
  };

  const fmtDate = (iso?: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? '—'
      : d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className={cn('rounded-2xl bg-white p-6 shadow-sm', className)}>
      <h3 className="mb-4 text-base font-bold text-[#191B23]">{t('topStolenItems')}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#F1F5F9]">
              <th className="whitespace-nowrap pb-3 pr-2 text-left text-[10px] font-bold uppercase tracking-wide text-[#94A3B8]">{t('productCode')}</th>
              <th className="whitespace-nowrap pb-3 pr-2 text-left text-[10px] font-bold uppercase tracking-wide text-[#94A3B8]">{t('reportsProduct')}</th>
              <th className="whitespace-nowrap px-2 pb-3 text-right text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] last:pr-0">{t('lostQuantity')}</th>
              <th className="whitespace-nowrap px-2 pb-3 text-right text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] last:pr-0">{t('incidents')}</th>
              <th className="whitespace-nowrap px-2 pb-3 text-right text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] last:pr-0">{t('lostValue')}</th>
              <th className="whitespace-nowrap px-2 pb-3 text-right text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] last:pr-0">{t('lastIncident')}</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-sm text-slate-400">
                  {t('noLostItemsInPeriod')}
                </td>
              </tr>
            )}
            {page.pageItems.map((item, i) => (
              <tr key={`${item.code}-${i}`} className="border-b border-[#F8FAFC] last:border-0">
                <td className="py-3 pr-2 font-bold text-[#191B23]">{item.code}</td>
                <td className="py-3 pr-2 text-[#434655]">{item.name}</td>
                <td className="px-2 py-3 text-right font-bold text-[#DC2626]">{item.quantityLost.toLocaleString()}</td>
                <td className="px-2 py-3 text-right text-[#434655]">{item.incidents.toLocaleString()}</td>
                <td className="px-2 py-3 text-right font-bold text-[#DC2626]">{fmtMoney(item.lostValue, item.currency)}</td>
                <td className="px-2 py-3 text-right text-[#434655]">{fmtDate(item.lastIncidentAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {page.hasItems && (
        <Pagination
          variant="numbered"
          currentPage={page.currentPage}
          totalPages={page.totalPages}
          totalItems={page.totalItems}
          pageSize={page.pageSize}
          onPageChange={page.setPage}
          itemLabel={t('products')}
          className="mt-4 border-t border-[#F1F5F9] pt-4"
        />
      )}
    </div>
  );
}
